import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SaveIcon } from 'lucide-react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

const InvestorFoundationSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        section_tag: '',
        section_heading: '',
        lead_text: '',
        secondary_text: '',
        primary_image_url: '',
        primary_image_badge: '',
        secondary_image_url: ''
    });
    const [primaryImageFile, setPrimaryImageFile] = useState(null);
    const [primaryImagePreview, setPrimaryImagePreview] = useState(null);
    const [secondaryImageFile, setSecondaryImageFile] = useState(null);
    const [secondaryImagePreview, setSecondaryImagePreview] = useState(null);

    // Fetch settings
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/investor-foundation/settings');
            if (response.data.success) {
                setSettings(response.data.data);
                // Set image previews from existing URLs
                if (response.data.data.primary_image_url) {
                    setPrimaryImagePreview(`http://localhost:8001${response.data.data.primary_image_url}`);
                }
                if (response.data.data.secondary_image_url) {
                    setSecondaryImagePreview(`http://localhost:8001${response.data.data.secondary_image_url}`);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle primary image selection
    const handlePrimaryImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPrimaryImageFile(file);
                setPrimaryImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle secondary image selection
    const handleSecondaryImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSecondaryImageFile(file);
                setSecondaryImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);

            const formData = new FormData();
            formData.append('section_tag', settings.section_tag);
            formData.append('section_heading', settings.section_heading);
            formData.append('lead_text', settings.lead_text);
            formData.append('secondary_text', settings.secondary_text);
            formData.append('primary_image_badge', settings.primary_image_badge);

            // Only append image URLs if no new files are uploaded
            if (!primaryImageFile) {
                formData.append('primary_image_url', settings.primary_image_url);
            }
            if (!secondaryImageFile) {
                formData.append('secondary_image_url', settings.secondary_image_url);
            }

            // Append image files if selected
            if (primaryImageFile) {
                formData.append('primary_image', primaryImageFile);
            }
            if (secondaryImageFile) {
                formData.append('secondary_image', secondaryImageFile);
            }

            await axios.put('/api/admin/investor-foundation/settings', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Settings updated successfully');
            // Reset file states and refetch to get new URLs
            setPrimaryImageFile(null);
            setSecondaryImageFile(null);
            fetchSettings();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Our Foundation - Settings</h1>
                <p className="text-slate-500 mt-1">Manage the "Our Foundation" section content</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
                {/* Section Tag */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Section Tag
                    </label>
                    <input
                        type="text"
                        name="section_tag"
                        value={settings.section_tag}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Our Foundation"
                    />
                </div>

                {/* Section Heading */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Section Heading
                    </label>
                    <input
                        type="text"
                        name="section_heading"
                        value={settings.section_heading}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Building Value With Purpose"
                    />
                </div>

                {/* Lead Text */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Lead Text (Main Paragraph)
                    </label>
                    <textarea
                        name="lead_text"
                        value={settings.lead_text}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="At Aham, our growth is driven by clarity of purpose..."
                    />
                </div>

                {/* Secondary Text */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Secondary Text (Second Paragraph)
                    </label>
                    <textarea
                        name="secondary_text"
                        value={settings.secondary_text}
                        onChange={handleChange}
                        rows="2"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="For us, success is not just about numbers..."
                    />
                </div>

                {/* Primary Image Upload */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Primary Image
                    </label>
                    <p className="text-xs text-slate-500 mb-3">
                        📐 Recommended: 800 x 600 pixels | Formats: WebP, PNG, JPEG
                    </p>
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePrimaryImageChange}
                                    className="hidden"
                                />
                                <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm font-medium text-slate-600">
                                    {primaryImagePreview ? 'Change Primary Image' : 'Upload Primary Image'}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                            </label>
                        </div>
                        {primaryImagePreview && (
                            <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                <img
                                    src={primaryImagePreview}
                                    alt="Primary Preview"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Primary Image Badge */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Primary Image Badge Text
                    </label>
                    <input
                        type="text"
                        name="primary_image_badge"
                        value={settings.primary_image_badge}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Sustainable Growth"
                    />
                </div>

                {/* Secondary Image Upload */}
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Secondary Image
                    </label>
                    <p className="text-xs text-slate-500 mb-3">
                        📐 Recommended: 800 x 600 pixels | Formats: WebP, PNG, JPEG
                    </p>
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSecondaryImageChange}
                                    className="hidden"
                                />
                                <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm font-medium text-slate-600">
                                    {secondaryImagePreview ? 'Change Secondary Image' : 'Upload Secondary Image'}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                            </label>
                        </div>
                        {secondaryImagePreview && (
                            <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                <img
                                    src={secondaryImagePreview}
                                    alt="Secondary Preview"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <SaveIcon size={20} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default InvestorFoundationSettingsPage;
